import { useCallback, useRef, useState } from 'react';
import * as tus from 'tus-js-client';
import LivepeerModel from 'Models/Livepeer';
import livepeerUtils from 'Utils/livepeerUtils';
import { IRequestAssetUploadResponse } from 'Models/Livepeer/types';

export type VideoUploadStatus = 'pending' | 'uploading' | 'completed' | 'failed';

export interface VideoUpload {
    id: string;
    file: File;
    status: VideoUploadStatus;
    progress: number;
    error?: string;
    playbackId?: string;
    livePeerAssetId?: string;
    thumbnail?: string;
}

const MAX_VIDEO_SIZE_KB = 100000; // 100MB

export const useVideoUpload = () => {
    const [videos, setVideos] = useState<VideoUpload[]>([]);
    const uploadInstancesRef = useRef<Map<string, tus.Upload>>(new Map());

    /**
     * Validates video file size
     */
    const validateVideoFile = useCallback((file: File): { isValid: boolean; error?: string } => {
        const fileSizeKB = file.size / 1024;

        if (fileSizeKB > MAX_VIDEO_SIZE_KB) {
            return {
                isValid: false,
                error: `Video file size must be less than ${MAX_VIDEO_SIZE_KB / 1000}MB`,
            };
        }

        if (!file.type?.includes('video')) {
            return {
                isValid: false,
                error: 'File must be a video',
            };
        }

        return { isValid: true };
    }, []);

    /**
     * Generates a thumbnail from video file
     */
    const generateThumbnail = useCallback((file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const video = document.createElement('video');
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            if (!ctx) {
                reject(new Error('Could not get canvas context'));
                return;
            }

            video.preload = 'metadata';
            video.onloadedmetadata = () => {
                video.currentTime = 0.1; // Seek to 0.1 seconds
            };

            video.onseeked = () => {
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                const thumbnail = canvas.toDataURL('image/jpeg', 0.7);
                resolve(thumbnail);
            };

            video.onerror = () => {
                reject(new Error('Failed to load video'));
            };

            video.src = URL.createObjectURL(file);
        });
    }, []);

    /**
     * Starts uploading a video file
     */
    const startUpload = useCallback(
        async (file: File): Promise<string> => {
            const validation = validateVideoFile(file);
            if (!validation.isValid) {
                throw new Error(validation.error);
            }

            const videoId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

            // Generate thumbnail
            let thumbnail: string | undefined;
            try {
                thumbnail = await generateThumbnail(file);
            } catch (error) {
                console.warn('Failed to generate thumbnail:', error);
            }

            // Add video to state with pending status
            const newVideo: VideoUpload = {
                id: videoId,
                file,
                status: 'pending',
                progress: 0,
                thumbnail,
            };

            setVideos((prev) => [...prev, newVideo]);

            try {
                // Request upload URL from Livepeer
                const uploadResponse: IRequestAssetUploadResponse = await LivepeerModel.requestAssetUpload(file.name);

                // Update status to uploading
                setVideos((prev) =>
                    prev.map((v) =>
                        v.id === videoId
                            ? {
                                  ...v,
                                  status: 'uploading',
                                  progress: 0,
                              }
                            : v,
                    ),
                );

                // Start TUS upload
                const upload = await livepeerUtils.tusUploadAsset(
                    file,
                    uploadResponse.tusEndpoint,
                    (uploadPercentage: number) => {
                        // Update progress
                        setVideos((prev) =>
                            prev.map((v) =>
                                v.id === videoId
                                    ? {
                                          ...v,
                                          progress: uploadPercentage,
                                      }
                                    : v,
                            ),
                        );
                    },
                    () => {
                        // On success
                        setVideos((prev) =>
                            prev.map((v) =>
                                v.id === videoId
                                    ? {
                                          ...v,
                                          status: 'completed',
                                          progress: 100,
                                          playbackId: uploadResponse.asset.playbackId,
                                          livePeerAssetId: uploadResponse.asset.id,
                                      }
                                    : v,
                            ),
                        );
                        uploadInstancesRef.current.delete(videoId);
                    },
                    (error: Error) => {
                        // On error
                        setVideos((prev) =>
                            prev.map((v) =>
                                v.id === videoId
                                    ? {
                                          ...v,
                                          status: 'failed',
                                          error: error.message || 'Upload failed',
                                      }
                                    : v,
                            ),
                        );
                        uploadInstancesRef.current.delete(videoId);
                    },
                );

                // Store upload instance for cancellation
                uploadInstancesRef.current.set(videoId, upload);
            } catch (error: any) {
                setVideos((prev) =>
                    prev.map((v) =>
                        v.id === videoId
                            ? {
                                  ...v,
                                  status: 'failed',
                                  error: error?.message || 'Failed to start upload',
                              }
                            : v,
                    ),
                );
                throw error;
            }

            return videoId;
        },
        [validateVideoFile, generateThumbnail],
    );

    /**
     * Cancels an ongoing upload
     */
    const cancelUpload = useCallback((videoId: string) => {
        const upload = uploadInstancesRef.current.get(videoId);
        if (upload) {
            upload.abort();
            uploadInstancesRef.current.delete(videoId);
        }
        removeVideo(videoId);
    }, []);

    /**
     * Retries a failed upload
     */
    const retryUpload = useCallback(
        async (videoId: string) => {
            const video = videos.find((v) => v.id === videoId);
            if (!video || video.status !== 'failed') {
                return;
            }

            try {
                await startUpload(video.file);
                // Remove the old failed video
                setVideos((prev) => prev.filter((v) => v.id !== videoId));
            } catch (error) {
                console.error('Retry upload failed:', error);
            }
        },
        [videos, startUpload],
    );

    /**
     * Removes a video from the list
     */
    const removeVideo = useCallback((videoId: string) => {
        const upload = uploadInstancesRef.current.get(videoId);
        if (upload) {
            upload.abort();
            uploadInstancesRef.current.delete(videoId);
        }
        setVideos((prev) => prev.filter((v) => v.id !== videoId));
    }, []);

    /**
     * Gets completed videos in the format required by sendMessage
     */
    const getCompletedVideos = useCallback(() => {
        return videos
            .filter((v) => v.status === 'completed' && v.playbackId && v.livePeerAssetId)
            .map((v) => ({
                playbackId: v.playbackId!,
                livePeerAssetId: v.livePeerAssetId!,
            }));
    }, [videos]);

    /**
     * Checks if any video is currently uploading
     */
    const isUploading = useCallback(() => {
        return videos.some((v) => v.status === 'uploading');
    }, [videos]);

    /**
     * Clears all videos
     */
    const clearVideos = useCallback(() => {
        // Cancel all ongoing uploads
        uploadInstancesRef.current.forEach((upload) => {
            upload.abort();
        });
        uploadInstancesRef.current.clear();
        setVideos([]);
    }, []);

    return {
        videos,
        startUpload,
        cancelUpload,
        retryUpload,
        removeVideo,
        getCompletedVideos,
        isUploading,
        clearVideos,
        validateVideoFile,
    };
};

export default useVideoUpload;
